import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormArray, FormBuilder, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Editor, Toolbar } from "ngx-editor";
import { CommonSelectionInterface } from "src/app/share/interface/common.interface";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { O2OPartnerProtectionService } from "../../services/o2o-partner-protection.service";
import { ValidatorFunction } from "../../../share/function/validator.function";
import {
  O2ODocumentInterface,
  O2OPartnerProtectionAddInterface,
  O2OPartnerProtectionEditInterface,
  O2OPartnerProtectionListingInterface
} from "../../interfaces/o2o-partner.interface";
import { AlertService } from "../../../share/service/alert.service";
import { ENTER } from "@angular/cdk/keycodes";
import { MatChipInputEvent } from "@angular/material/chips";
import {
  accordionEditorOptions,
  isInAccordion,
  toggleAccordion
} from "./accordion";
import {
  ProtectionPreviewData,
  ProtectionPreviewDialogComponent
} from "../protection-preview-dialog/protection-preview-dialog.component";

export interface ProtectionDialogData {
  id: string; // partner id
  protection?: O2OPartnerProtectionListingInterface;
}

@Component({
  selector: "app-add-protection-dialog",
  templateUrl: "./add-protection-dialog.component.html",
  styleUrls: ["./add-protection-dialog.component.scss"]
})
export class AddProtectionDialogComponent implements OnInit, OnDestroy {
  isEditMode = false;
  readonly separatorKeysCodes: number[] = [ENTER];

  form = this.fb.group({
    type: ["", Validators.required],
    insurers: ["", Validators.required],
    title: ["", Validators.required],
    catalog: [[] as File[], [ValidatorFunction.fileSize(5)]],
    description: ["", Validators.required],
    to: this.fb.array<string>([], [ValidatorFunction.minValidEmails(1)]),
    cc: this.fb.array<string>([]),
    masterPolicyNo: ["", Validators.required],
  });

  protectionTypes: CommonSelectionInterface[] = [];
  insurers: CommonSelectionInterface[] = [];

  // local copies so toggling deletion does not mutate the listing data
  existingCatalogs: O2ODocumentInterface[] = [];

  editor: Editor;
  isAccordionActive = false;
  toolbar: Toolbar = [
    ["bold", "italic"],
    ["underline", "strike"],
    ["code", "blockquote", "horizontal_rule"],
    ["ordered_list", "bullet_list"],
    [{ heading: ["h1", "h2", "h3", "h4", "h5", "h6"] }],
    ["link", "image"],
    ["text_color", "background_color"],
    ["align_left", "align_center", "align_right", "align_justify"]
  ];

  private destroy$ = new Subject<void>();

  // Serialized baseline of the emails as seeded into the form, so the dirty
  // check compares like with like instead of against the raw stored string.
  private originalToEmails = "";
  private originalCcEmails = "";

  constructor(
    private alertService: AlertService,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<AddProtectionDialogComponent>,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: ProtectionDialogData,
    private o2oPartnerProtectionService: O2OPartnerProtectionService
  ) {
    this.isEditMode = !!this.data.protection;
    this.existingCatalogs = (this.data.protection?.catalog ?? []).map((doc) => ({
      ...doc,
      UI_isDeleted: false
    }));
  }

  get toEmails(): FormArray {
    return this.form.get("to") as FormArray;
  }

  get ccEmails(): FormArray {
    return this.form.get("cc") as FormArray;
  }

  get isFormChanged(): boolean {
    if (!this.isEditMode) return true;

    const protection = this.data.protection;
    const { type, insurers, title, description, to, cc, catalog, masterPolicyNo } =
      this.form.value;

    const insurerEmails = this.serializeEmails(to);
    const insurerCcEmails = this.serializeEmails(cc);

    // Check if a new file is selected in the upload component
    const isFilesChanged = catalog && catalog.length > 0;

    // Check if any existing document was toggled for deletion
    const isExistingDocumentDeleted = this.existingCatalogs.some(
      (doc) => doc.UI_isDeleted
    );

    return (
      type !== protection.protectionTypeId ||
      insurers !== protection.insurerId ||
      title !== protection.title ||
      description !== protection.description ||
      insurerEmails !== this.originalToEmails ||
      insurerCcEmails !== this.originalCcEmails ||
      masterPolicyNo !== protection?.masterPolicyNo ||
      isFilesChanged ||
      isExistingDocumentDeleted
    );
  }

  ngOnInit(): void {
    this.editor = new Editor(accordionEditorOptions());
    // Drives the Accordion button's active state, which switches it from
    // inserting an accordion to removing the one around the cursor.
    this.editor.update
      .pipe(takeUntil(this.destroy$))
      .subscribe((view) => (this.isAccordionActive = isInAccordion(view.state)));

    this.getProtectionTypes();
    this.handleTypeChanges();

    if (this.isEditMode) {
      this.form.patchValue({
        title: this.data.protection.title,
        description: this.data.protection.description,
        type: this.data.protection.protectionTypeId,
        insurers: this.data.protection.insurerId,
        masterPolicyNo: this.data.protection.masterPolicyNo,
      });

      this.splitEmails(this.data.protection.insurerEmails).forEach((email) => {
        this.toEmails.push(this.createEmailControl(email));
      });

      this.splitEmails(this.data.protection?.insurerCcEmails).forEach(
        (ccEmail) => {
          this.ccEmails.push(this.createEmailControl(ccEmail));
        }
      );

      this.originalToEmails = this.serializeEmails(this.toEmails.value);
      this.originalCcEmails = this.serializeEmails(this.ccEmails.value);

      // Stored data can hold malformed addresses, which block the form. Touch
      // the arrays so the reason is visible as soon as the dialog opens.
      if (this.toEmails.invalid) this.toEmails.markAsTouched();
      if (this.ccEmails.invalid) this.ccEmails.markAsTouched();

      this.form.get("catalog").clearValidators();
      this.form.get("catalog").setValidators([ValidatorFunction.fileSize(5)]);
      this.form.get("catalog").updateValueAndValidity();
    }
  }

  onToggleAccordion(): void {
    const { view } = this.editor;
    toggleAccordion(view.state, view.dispatch);
    view.focus();
  }

  ngOnDestroy(): void {
    this.editor.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  getProtectionTypes(): void {
    this.o2oPartnerProtectionService.getProtectionTypes().subscribe((types) => {
      this.protectionTypes = types;
    });
  }

  handleTypeChanges(): void {
    this.form
      .get("type")
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((typeId) => {
        if (typeId) {
          this.o2oPartnerProtectionService
            .getInsurersByProtectionTypeId(typeId)
            .subscribe((insurers) => {
              this.insurers = insurers;
            });
        } else {
          this.insurers = [];
          this.form.get("insurers").setValue("");
        }
      });
  }

  // The chip list is not bound to the form array, so it never marks it touched
  // on its own. Do it manually so validation messages can surface.
  markEmailsTouched(field: "to" | "cc"): void {
    (field === "to" ? this.toEmails : this.ccEmails).markAsTouched();
  }

  isEmailInvalid(index: number, field: "to" | "cc"): boolean {
    const control = field === "to" ? this.toEmails : this.ccEmails;
    return !!control.at(index)?.invalid;
  }

  hasInvalidEmail(field: "to" | "cc"): boolean {
    const control = field === "to" ? this.toEmails : this.ccEmails;
    return control.controls.some((email) => email.invalid);
  }

  addEmail(event: MatChipInputEvent, field: "to" | "cc"): void {
    const value = (event.value || "").trim().toLowerCase();
    if (!value) return;

    this.markEmailsTouched(field);

    if (this.isDuplicateEmail(value)) {
      this.alertService.openSnackBar("Email already exists");
    } else {
      this.processEmailAddition(value, field);
    }

    if (event.chipInput) {
      event.chipInput.clear();
    }
  }

  removeEmail(index: number, field: "to" | "cc"): void {
    const control = field === "to" ? this.toEmails : this.ccEmails;
    control.removeAt(index);
    this.markEmailsTouched(field);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  get canPreview(): boolean {
    const { title, description } = this.form.getRawValue();
    // An emptied editor emits "<p></p>" rather than "".
    return !!title?.trim() || (!!description && description !== "<p></p>");
  }

  onPreview(): void {
    const { title, description, catalog } = this.form.getRawValue();
    const data: ProtectionPreviewData = {
      title,
      descriptionHtml: description,
      // Mirrors what will be saved: kept existing catalogs, then new uploads.
      catalogFileNames: [
        ...this.existingCatalogs
          .filter((doc) => !doc.UI_isDeleted)
          .map((doc) => doc.fileName),
        ...(catalog ?? []).map((file) => file.name)
      ]
    };

    this.dialog.open(ProtectionPreviewDialogComponent, {
      width: "760px",
      maxWidth: "95vw",
      data
    });
  }

  onSave(): void {
    if (!this.form.valid || !this.isFormChanged) {
      return;
    }

    const payload = this.isEditMode
      ? this.getEditPayload()
      : this.getAddPayload();

    const request$ = this.isEditMode
      ? this.o2oPartnerProtectionService.update(this.data.id, payload as O2OPartnerProtectionEditInterface)
      : this.o2oPartnerProtectionService.add(this.data.id, payload as O2OPartnerProtectionAddInterface);

    request$.subscribe({
      next: () => {
        const action = this.isEditMode ? "updated" : "added";
        this.alertService.openSnackBar(`Protection ${action} successfully`);
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.alertService.openSnackBar(err.error);
      }
    });
  }

  toggleDeletion(doc: O2ODocumentInterface): void {
    doc.UI_isDeleted = !doc.UI_isDeleted;
  }

  private isDuplicateEmail(email: string): boolean {
    const allEmails: string[] = [
      ...this.toEmails.value,
      ...this.ccEmails.value
    ];
    // Stored addresses keep their original casing, so compare case-insensitively.
    const normalized = email.trim().toLowerCase();
    return allEmails.some(
      (existing) => (existing ?? "").trim().toLowerCase() === normalized
    );
  }

  private processEmailAddition(value: string, field: "to" | "cc"): void {
    const control = field === "to" ? this.toEmails : this.ccEmails;
    const emailControl = this.createEmailControl(value);

    if (emailControl.invalid) {
      this.alertService.openSnackBar("Invalid email format");
      return;
    }

    // if (field === "to") {
    //   control.clear();
    // }
    control.push(emailControl);
  }

  private getEditPayload(): O2OPartnerProtectionEditInterface {
    const { catalog, title, type, insurers, description, to, cc, masterPolicyNo } =
      this.form.value;
    const protection = this.data.protection;

    const insurerEmails = this.serializeEmails(to);
    const insurerCcEmails = this.serializeEmails(cc);

    const payload: O2OPartnerProtectionEditInterface = {
      id: protection.id
    };

    if (type !== protection.protectionTypeId) payload.protectionTypeId = type;
    if (insurers !== protection.insurerId) payload.insurerId = insurers;
    if (title !== protection.title) payload.title = title;
    if (description !== protection.description)
      payload.description = description;
    if (insurerEmails !== this.originalToEmails)
      payload.insurerEmails = insurerEmails;
    if (masterPolicyNo !== protection?.masterPolicyNo) payload.masterPolicyNo = masterPolicyNo;
    if (insurerCcEmails !== this.originalCcEmails)
      payload.insurerCcEmails = insurerCcEmails;
    if (catalog?.length > 0) payload.catalog = catalog;

    const removedIds = this.existingCatalogs
      .filter((doc) => doc.UI_isDeleted)
      .map((doc) => doc.documentId);

    if (removedIds.length > 0) payload.removedCatalogFileIds = removedIds;

    return payload;
  }

  private getAddPayload(): O2OPartnerProtectionAddInterface {
    const { catalog, title, type, insurers, description, to, cc, masterPolicyNo } =
      this.form.value;

    const insurerEmails = this.serializeEmails(to);
    const insurerCcEmails = this.serializeEmails(cc);

    return {
      catalog,
      title,
      description,
      protectionTypeId: type,
      insurerId: insurers,
      insurerEmails: insurerEmails,
      insurerCcEmails: insurerCcEmails,
      masterPolicyNo: masterPolicyNo,
    };
  }

  private createEmailControl(value: any) {
    return this.fb.control(value, [Validators.required, Validators.email]);
  }

  private serializeEmails(
    values: (string | null | undefined)[] | null | undefined
  ): string {
    return (values ?? []).filter(Boolean).join(";");
  }

  private splitEmails(emails: string | null | undefined): string[] {
    return (emails || "")
      .split(";")
      .map((email) => email.trim())
      .filter(Boolean);
  }
}
